import { useEffect, useState } from "react";
import { NotebookPen, Plus, Trash2, ChevronLeft } from "lucide-react";

/* Notes — pocket notepad. Stored on the handset (localStorage per server/citizen),
   so it survives reloads in preview and in-game without touching the hub. */

export default function NotesApp({ serverId, pc, accent, sfx }) {
  const KEY = `nc_notes_${serverId}_${pc || "demo"}`;
  const [notes, setNotes] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
  });
  const [open, setOpen] = useState(null); // note index
  const [draft, setDraft] = useState("");

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(notes)); }, [notes, KEY]);

  const add = () => {
    if (!draft.trim()) return;
    sfx?.keyTick();
    setNotes((n) => [{ id: Date.now(), body: draft.trim(), at: new Date().toISOString() }, ...n]);
    setDraft("");
  };

  if (open !== null && notes[open]) {
    const n = notes[open];
    return (
      <div data-testid="note-open" className="flex flex-col min-h-full">
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/10">
          <button data-testid="note-back" onClick={() => setOpen(null)} className="text-white/60 hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
          <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/40">{new Date(n.at).toLocaleString()}</p>
          <button data-testid="note-delete" onClick={() => { sfx?.error?.(); setNotes((ns) => ns.filter((x) => x.id !== n.id)); setOpen(null); }}
            className="ml-auto text-red-400/70 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
        </div>
        <p className="flex-1 p-4 font-mono text-[11px] text-white/85 leading-relaxed whitespace-pre-wrap">{n.body}</p>
      </div>
    );
  }

  return (
    <div data-testid="notes-app" className="p-3 flex flex-col gap-2 min-h-full">
      <div className="flex items-center gap-2 px-1 pb-1">
        <NotebookPen className="w-4 h-4 text-amber-200" />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">Notes</p>
        <p className="ml-auto font-mono text-[7px] text-white/40 uppercase tracking-[0.1em]">{notes.length} saved</p>
      </div>
      <div className="rounded-2xl bg-white/5 border border-white/10 p-3 flex flex-col gap-2">
        <textarea data-testid="note-draft" value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} maxLength={500}
          placeholder="corner prices, safe codes, license plates…"
          className="bg-transparent font-mono text-[10px] text-white placeholder:text-white/25 focus:outline-none resize-none" />
        <button data-testid="note-add" onClick={add} disabled={!draft.trim()}
          className="self-end flex items-center gap-1.5 font-mono text-[8px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full text-black disabled:opacity-40"
          style={{ backgroundColor: accent }}><Plus className="w-3 h-3" /> Save note</button>
      </div>
      {notes.length === 0 && <p data-testid="notes-empty" className="font-mono text-[10px] text-white/40 text-center mt-8">Nothing jotted down yet</p>}
      {notes.map((n, i) => (
        <button key={n.id} data-testid={`note-${i}`} onClick={() => { sfx?.keyTick(); setOpen(i); }}
          className="rounded-2xl bg-white/5 border border-white/10 p-3 text-left hover:bg-white/10 transition-colors">
          <p className="font-mono text-[10px] text-white/85 truncate">{n.body}</p>
          <p className="font-mono text-[7px] text-white/35 mt-1">{new Date(n.at).toLocaleDateString()} · {new Date(n.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
        </button>
      ))}
      <p className="mt-auto font-mono text-[7px] text-white/30 text-center uppercase tracking-[0.2em]">stored on this handset only</p>
    </div>
  );
}
